import React, { useEffect, useMemo, useRef, useState } from "react";
import useBreadcrumbs from "../../../hooks/useBreadCrumbs";
import NavCrumbs from "../../../common/NavCrumbs";
import AddUpdateTeam from "./components/AddUpdateTeam";
import { EMPLOYEE_ACTIONS, TEAM_HEADERS } from "./action/constant";
import TableRowsPerPage from "../../../common/TableRowsPerPage";
import CustomPagination from "../../../common/Pagination";
import DataTable from "../../../common/DataTable";
import SearchInput from "../../../common/InlineSearch";
import { getTeamsData } from "./action/fetchTeamsData";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/Store";
import { Container, Stack, Tab, Tabs } from "react-bootstrap";
import TeamsBillingDashboard from "./components/TeamsBillingDashboard";
import { fetcheaderinfo } from "../../../services/UserRegistrationApi";
import { WEB_ROLE } from "../../../common/Constant";
import AddUpdateUserMapping from "./components/AddUpdateUserMapping";
import UserMappingDashboard from "./components/UserMappingDashboard";

const TABS = ["Teams", "Billing"];

const TeamRegistration: React.FC = () => {
  const [showAddUpdateTeam, setShowAddUpdateTeam] = useState(false);
  const [showAddUpdateBilling, setShowAddUpdateBilling] = useState(false);
  const [showAddUpdateUserMapping, setShowAddUpdateUserMapping] =
    useState(false);
  const [showEditBilling, setShowEditBilling] = useState(false);
  const breadcrumbs = useBreadcrumbs();
  const [data, setData] = useState<any[]>([]); // Original dataset
  const [filteredData, setFilteredData] = useState<any[]>([]); // Filtered dataset// Filtered dataset
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15); // Default value set to 10
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchQuerylength, setSearchQuerylength] = useState<string>("");
  const searchInputRef = useRef<any>(null);
  const [totalRows, setTotalRows] = useState<any>("");
  const [activeTab, setActiveTab] = useState<string>("Teams");
  const [teamId, setTeamId] = useState<number | null>(null);
  const [teamName, setTeamName] = useState<string>("");
  const [userRole, setUserRole] = useState("");
  const userID = useSelector((state: RootState) => state.auth.userid);

  useEffect(() => {
    const getloggedInUserRole = async () => {
      try {
        const userCredentialData = await fetcheaderinfo(userID);
        if (userCredentialData) {
          setUserRole(userCredentialData?.currentAppRoleDesc);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (userID) {
      getloggedInUserRole();
    }
  }, []);

  const allowedRolesForBilling = [
    WEB_ROLE.App_Admin,
    WEB_ROLE.System_Admin,
    WEB_ROLE.Billing_Specialist,
    WEB_ROLE.Supervisor,
  ];

  const isBilling = allowedRolesForBilling.includes(userRole);
  const tabs = isBilling ? ["Teams", "User Mapping"] : ["Teams"];

  const handleCreateTeamClick = () => {
    setShowAddUpdateTeam(true);
    setTeamId(null);
    setTeamName("");
  };

  const handleCancel = () => {
    setSelectedUserData("");
    setCurrentPage(1);
    setAction("");
    setShowAddUpdateTeam(false);
    handleReset();
  };

  const handleOnclose = () => {
    setShowAddUpdateBilling(false);
    setShowEditBilling(false);
  };

  const fetchTeams = async (
    query: string = "",
    pageNo: number = 1,
    pageSize: number = itemsPerPage,
    key: any = "",
    direction: any = "DESC",
  ) => {
    const userdata = {
      searchString: query,
      pageNo,
      pageSize,
      sortColumn: key,
      sortOrder: direction,
      triggerById: userID,
    };

    try {
      const teamsData = await getTeamsData(userdata);

      if (teamsData) {
        setData(teamsData);
        setFilteredData(teamsData);
        setTotalPages(teamsData[0]?.totalPages || 1);
        setTotalRows(teamsData[0]?.totalCount || null);
      } else {
        console.error("No data found in teamsData.result");
      }
    } catch (error) {
      console.error("Error fetching teams data:", error);
    }
  };

  const restoreLatestTeam = async (id: number) => {
    try {
      const userdata = {
        searchString: "",
        pageNo: 1,
        pageSize: itemsPerPage,
        sortColumn: "",
        sortOrder: "DESC",
        triggerById: userID,
      };

      const teamsData = await getTeamsData(userdata);

      if (teamsData?.length) {
        const matchedTeam = teamsData.find((team: any) => team.teamId === id);

        if (matchedTeam) {
          setSelectedUserData(matchedTeam);
          setAction("Edit");
          setShowAddUpdateTeam(true);
        }
      }
    } catch (err) {
      console.error("Failed to restore team", err);
    }
  };

  useEffect(() => {
    if (activeTab === "Teams" && teamId) {
      restoreLatestTeam(teamId);
    }
  }, [activeTab]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      fetchTeams(searchQuery, currentPage, itemsPerPage);
    } else {
      fetchTeams();
    }
  }, [itemsPerPage]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    fetchTeams(query, 1, itemsPerPage);
    setCurrentPage(1); // Reset to first page on search
  };

  // Fetch data whenever itemsPerPage changes

  //const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    fetchTeams(searchQuery, pageNumber); // Fetch data for the selected page
  };

  const currentPageData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setShowAddUpdateTeam(true); // Open AddNewUser component
    setSelectedUserData(row);
    setTeamId(row?.teamId);
    setTeamName(row?.teamname);
  };

  const handleSortAction = (row: any) => {
    fetchTeams(searchQuery, currentPage, itemsPerPage, row.key, row.direction);
  };

  // Reset the user list and search filters
  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery("");
    setSearchQuerylength("");
    fetchTeams();
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  const handleTabChange = (key: any) => {
    setActiveTab(key);
    setShowAddUpdateUserMapping(false);
  };

  const handleTeamSaved = (savedTeamId: number, savedTeamName: string) => {
    setTeamId(savedTeamId);
    setTeamName(savedTeamName);
  };

  const handleConfigureClick = () => {
    setShowAddUpdateBilling(true);
    setActiveTab("Billing");
  };

  const handleUserMappingConfigureClick = () => {
    setShowAddUpdateUserMapping(true);
    setActiveTab("User Mapping");
  };

  const handleUserMappingOnclose = () => {
    setShowAddUpdateUserMapping(false);
    setActiveTab("User Mapping");
  };

  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows,
      )} of ${totalRows} entries`;

  return (
    <div>
      <div className="breadcrumbg">
        <Container fluid className="px-5">
          {showAddUpdateBilling || showEditBilling ? (
            <Stack direction="horizontal" className="align-items-center">
              {/* Left side */}
              <div className="pt-0 d-flex flex-column align-items-start fade-in-left">
                <h4 className="mb-1">&nbsp; Teams</h4>
                <NavCrumbs
                  breadcrumbs={[
                    { name: "Master", slug: "master" },
                    { name: "Billing", slug: "billing" },
                  ]}
                />
              </div>

              {/* Right side */}
              <div className="ms-auto text-end">
                <strong>Team Name:</strong> {teamName}
              </div>
            </Stack>
          ) : (
            <Stack direction="horizontal" gap={2}>
              <div className="pt-0 flex-column d-flex align-items-start fade-in-left">
                <h4>Teams</h4>
                {!showAddUpdateTeam ? (
                  <NavCrumbs breadcrumbs={breadcrumbs} />
                ) : (
                  <Tabs
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                    className="Tab_borderBottom fade-in-right mb-15"
                  >
                    {tabs.map((tab) => (
                      <Tab
                        key={tab}
                        eventKey={tab}
                        title={tab}
                        disabled={!teamId}
                      />
                    ))}
                  </Tabs>
                )}
              </div>
              <div className="p-2 ms-auto">
                {!showAddUpdateTeam && activeTab === "Teams" ? (
                  <button
                    onClick={handleCreateTeamClick}
                    className="btn btn-primary d-flex align-items-center fade-in-right"
                  >
                    <span className="material-icons me-2">add</span>
                    Create Team
                  </button>
                ) : (
                  ""
                )}

                {!showAddUpdateBilling && activeTab === "Billing" && (
                  <button
                    onClick={handleConfigureClick}
                    className="btn btn-primary d-flex align-items-center fade-in-right"
                  >
                    <span className="material-icons me-2">add</span>
                    Configure
                  </button>
                )}

                {!showAddUpdateUserMapping && activeTab === "User Mapping" && (
                  <button
                    onClick={handleUserMappingConfigureClick}
                    className="btn btn-primary d-flex align-items-center fade-in-right"
                  >
                    <span className="material-icons me-2">add</span>
                    Create User Mapping
                  </button>
                )}
              </div>
            </Stack>
          )}
        </Container>
      </div>

      {!showAddUpdateTeam && activeTab === "Teams" && (
        <>
          <div className="main_wrapper_section">
            <div className="container-fluid px-5">
              <div className="d-flex   py-2 search_closer align-items-center">
                <div className="sectionTitle noborder">List</div>
                <SearchInput
                  ref={searchInputRef}
                  data={data}
                  searchKeys={[]}
                  onSearchResult={setFilteredData}
                  placeholder="Search"
                  onSearchQueryChange={handleSearchQueryChange} // Pass the handler
                  onInputChange={handleSearchQueryChangeForlength}
                />
                <button
                  onClick={handleReset}
                  className="reset_btn"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                  }}
                >
                  <span className="material-icons reset_ic0">restart_alt</span>
                </button>
              </div>
              <div className="borderB mb-3"></div>
              <div className="adjustHeightTb">
                <DataTable
                  headers={TEAM_HEADERS}
                  rows={filteredData} // Pass the filtered data
                  onAction={handleRowAction}
                  onSortAction={handleSortAction}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center mt-0">
                {/* Display entries text on the left */}
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-md-6 showingData_wrapper">
                      <span>{entriesText}</span>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex flex-row-reverse ">
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={paginate}
                        />
                        <TableRowsPerPage
                          value={itemsPerPage}
                          onSelect={handleRowsPerPageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Show AddUpdateTeam Form */}
      {showAddUpdateTeam && activeTab === "Teams" && (
        <AddUpdateTeam
          userData={selectedUserData}
          action={action}
          onCancel={handleCancel}
          userID={userID}
          onTeamSaved={handleTeamSaved}
        />
      )}

      {activeTab === "Billing" && (
        <TeamsBillingDashboard
          teamId={teamId}
          showAddUpdateBilling={showAddUpdateBilling}
          activeTab={activeTab}
          onCloseBilling={handleOnclose}
          setShowEditBilling={setShowEditBilling}
        />
      )}

      {activeTab === "User Mapping" && (
        <UserMappingDashboard
          teamId={teamId}
          showAddUpdateUserMapping={showAddUpdateUserMapping}
          setShowAddUpdateUserMapping={setShowAddUpdateUserMapping}
          onClose={handleUserMappingOnclose}
        />
      )}
    </div>
  );
};

export default TeamRegistration;
